import { useState, type Key } from 'react';
import {
  Tabs,
  Table,
  Button,
  Tag,
  Typography,
  Space,
  Modal,
  Form,
  Input,
  AutoComplete,
  Select,
  App,
  Popconfirm,
} from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useSearchParams } from 'react-router-dom';
import type { ContactMeta } from '@bom/shared';
import { useSessionStore } from '../../stores/sessionStore';
import {
  useMembers,
  useAddMember,
  useUpdateMember,
  useRemoveMember,
  useAssignableUsers,
  type MemberItem,
} from '../../api/members';
import {
  useContacts,
  useCreateContact,
  useUpdateContact,
  useDeleteContact,
} from '../../api/contacts';
import { useContactGroups, useImportGroup } from '../../api/contact-groups';
import { NoTenant } from '../../components/NoTenant';
import { ExcelImportButton } from '../../components/ExcelImportButton';
import { fmtTime, ROLE_LABEL } from '../../lib/format';
import { useRoleLabelOptions } from '../../api/role-labels';
import {
  parseContactSheet,
  parseMemberSheet,
  CONTACT_TEMPLATE_HEADERS,
  CONTACT_TEMPLATE_SAMPLE,
  MEMBER_TEMPLATE_HEADERS,
  MEMBER_TEMPLATE_SAMPLE,
  type ContactImportRow,
  type MemberImportRow,
} from '../../lib/excel';

function memberUid(m: MemberItem): string {
  return m.user_id ?? m.GSI3SK?.replace('USER#', '') ?? '—';
}

function MembersTab({ tid, canWrite }: { tid: string; canWrite: boolean }) {
  const { message } = App.useApp();
  const members = useMembers(tid);
  const addMember = useAddMember(tid);
  const updateMember = useUpdateMember(tid);
  const removeMember = useRemoveMember(tid);
  // 下拉选人：管理员/平台管理员可获取可分配用户目录（最小字段），排除已是成员的
  const usersQ = useAssignableUsers(tid, canWrite);
  const userMap = new Map((usersQ.data ?? []).map((u) => [u.id, u]));
  const emailToUser = new Map((usersQ.data ?? []).map((u) => [u.email.toLowerCase(), u]));
  const memberIdSet = new Set((members.data ?? []).map(memberUid));

  // Excel 导入成员：按邮箱匹配已有账号 + 角色
  const importMember = async (row: MemberImportRow) => {
    const u = emailToUser.get(row.email);
    if (!u) throw new Error(`未找到账号：${row.email}（请先在「用户管理」创建）`);
    if (memberIdSet.has(u.id)) throw new Error(`已是成员：${row.email}`);
    await addMember.mutateAsync({ user_id: u.id, role: row.role });
  };
  const userOptions = (usersQ.data ?? [])
    .filter((u) => !memberIdSet.has(u.id))
    .map((u) => ({
      value: u.id,
      label: `${u.name || '(无名)'}（${u.email}）${u.is_platform_admin ? ' · 平台' : ''}`,
    }));
  const [form] = Form.useForm();
  const [editForm] = Form.useForm();
  const [addOpen, setAddOpen] = useState(false);
  const [editUid, setEditUid] = useState<string | null>(null);
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [delDone, setDelDone] = useState(0);

  const onBulkRemove = async () => {
    setBulkDeleting(true);
    setDelDone(0);
    let done = 0;
    const results = await Promise.allSettled(
      selectedKeys.map((uid) =>
        removeMember.mutateAsync(uid).finally(() => {
          done += 1;
          setDelDone(done);
        }),
      ),
    );
    const ok = results.filter((r) => r.status === 'fulfilled').length;
    const fail = results.length - ok;
    setBulkDeleting(false);
    setSelectedKeys([]);
    if (fail === 0) message.success(`已移除 ${ok} 个成员`);
    else message.warning(`移除 ${ok} 个，${fail} 个失败`);
  };

  const onAdd = () =>
    form.validateFields().then((v) =>
      addMember.mutate(v, {
        onSuccess: () => {
          message.success('成员已添加');
          setAddOpen(false);
          form.resetFields();
        },
        onError: () => message.error('添加失败（user_id 是否有效 / 已是成员）'),
      }),
    );

  const onEdit = () =>
    editForm.validateFields().then((v) => {
      if (!editUid) return;
      updateMember.mutate(
        { uid: editUid, body: v },
        {
          onSuccess: () => {
            message.success('已更新');
            setEditUid(null);
          },
          onError: () => message.error('更新失败'),
        },
      );
    });

  return (
    <>
      {canWrite && (
        <Space style={{ marginBottom: 12 }}>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setAddOpen(true)}>
            添加成员
          </Button>
          <ExcelImportButton<MemberImportRow>
            templateName="产品成员导入模板"
            templateHeaders={MEMBER_TEMPLATE_HEADERS}
            templateSample={MEMBER_TEMPLATE_SAMPLE}
            parse={parseMemberSheet}
            importOne={importMember}
            onDone={() => members.refetch()}
            label="导入成员"
            previewColumns={[
              { title: '邮箱', dataIndex: 'email' },
              {
                title: '角色',
                dataIndex: 'role',
                editor: {
                  type: 'select',
                  options: [
                    { value: 'admin', label: '管理员' },
                    { value: 'editor', label: '编辑' },
                    { value: 'viewer', label: '审核员' },
                  ],
                },
              },
            ]}
            validateRow={(r) => {
              const email = String(r['email'] ?? '').toLowerCase();
              if (!email) return '缺少邮箱';
              const u = emailToUser.get(email);
              if (!u) return '邮箱在用户管理不存在（请先创建账号）';
              if (memberIdSet.has(u.id)) return '该用户已是本产品成员';
              return null;
            }}
          />
          {selectedKeys.length > 0 && (
            <Popconfirm
              title={`移除选中的 ${selectedKeys.length} 个成员？`}
              okText="移除"
              okButtonProps={{ danger: true }}
              cancelText="取消"
              onConfirm={onBulkRemove}
            >
              <Button danger loading={bulkDeleting}>
                {bulkDeleting
                  ? `移除中 ${delDone}/${selectedKeys.length}`
                  : `批量移除 (${selectedKeys.length})`}
              </Button>
            </Popconfirm>
          )}
        </Space>
      )}
      <Table<MemberItem>
        rowKey={(m) => memberUid(m)}
        loading={members.isLoading}
        dataSource={members.data ?? []}
        pagination={false}
        {...(canWrite
          ? {
              rowSelection: {
                selectedRowKeys: selectedKeys,
                onChange: (keys: Key[]) => setSelectedKeys(keys.map(String)),
              },
            }
          : {})}
        columns={[
          {
            title: '用户',
            key: 'user',
            render: (_: unknown, m) => {
              const uid = memberUid(m);
              // 优先用后端带回的 name/email；否则用可分配用户目录映射；再否则回退 UUID
              const u = userMap.get(uid);
              const name = m.name || u?.name;
              const email = m.email || u?.email;
              return name || email ? (
                <span>
                  {name || '(无名)'}{' '}
                  {email && <Typography.Text type="secondary">（{email}）</Typography.Text>}
                </span>
              ) : (
                <Typography.Text type="secondary" copyable={{ text: uid }}>
                  {uid}
                </Typography.Text>
              );
            },
          },
          {
            title: '角色',
            dataIndex: 'role',
            width: 140,
            render: (r: string) => <Tag color="blue">{ROLE_LABEL[r] ?? r}</Tag>,
          },
          {
            title: '状态',
            dataIndex: 'status',
            width: 90,
            render: (s: string) => (
              <Tag color={s === 'active' ? 'green' : 'red'}>{s === 'active' ? '启用' : '停用'}</Tag>
            ),
          },
          { title: '加入时间', dataIndex: 'added_at', width: 170, render: fmtTime },
          ...(canWrite
            ? [
                {
                  title: '操作',
                  key: 'action',
                  width: 150,
                  render: (_: unknown, m: MemberItem) => (
                    <Space size="small">
                      <Button
                        size="small"
                        type="link"
                        onClick={() => {
                          setEditUid(memberUid(m));
                          editForm.setFieldsValue({ role: m.role, status: m.status });
                        }}
                      >
                        编辑
                      </Button>
                      <Popconfirm
                        title="移除该成员？"
                        onConfirm={() =>
                          removeMember.mutate(memberUid(m), {
                            onSuccess: () => message.success('已移除'),
                            onError: () => message.error('移除失败'),
                          })
                        }
                      >
                        <Button size="small" type="link" danger>
                          移除
                        </Button>
                      </Popconfirm>
                    </Space>
                  ),
                },
              ]
            : []),
        ]}
      />

      <Modal
        title="添加成员"
        open={addOpen}
        onOk={onAdd}
        confirmLoading={addMember.isPending}
        onCancel={() => setAddOpen(false)}
        destroyOnClose
      >
        <Typography.Paragraph type="secondary">
          从已创建的用户中选择加入本产品（用户在「用户管理」中创建）。
        </Typography.Paragraph>
        <Form form={form} layout="vertical" preserve={false}>
          <Form.Item
            name="user_id"
            label="用户"
            rules={[{ required: true, message: '请选择用户' }]}
          >
            <Select
              showSearch
              optionFilterProp="label"
              placeholder="选择用户（可搜索邮箱/姓名）"
              loading={usersQ.isLoading}
              options={userOptions}
              notFoundContent={usersQ.isLoading ? '加载中…' : '无可添加的用户（都已是成员？）'}
            />
          </Form.Item>
          <Form.Item
            name="role"
            label="角色"
            initialValue="admin"
            rules={[{ required: true, message: '请选择角色' }]}
          >
            <Select
              options={[
                { value: 'admin', label: '管理员' },
                { value: 'editor', label: '编辑' },
                { value: 'viewer', label: '审核员' },
              ]}
            />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="编辑成员"
        open={!!editUid}
        onOk={onEdit}
        confirmLoading={updateMember.isPending}
        onCancel={() => setEditUid(null)}
        destroyOnClose
      >
        <Form form={editForm} layout="vertical" preserve={false}>
          <Form.Item name="role" label="角色">
            <Select
              options={[
                { value: 'admin', label: '管理员' },
                { value: 'editor', label: '编辑' },
                { value: 'viewer', label: '审核员' },
              ]}
            />
          </Form.Item>
          <Form.Item name="status" label="状态">
            <Select
              options={[
                { value: 'active', label: '启用' },
                { value: 'disabled', label: '停用' },
              ]}
            />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}

function ContactsTab({ tid, canWrite }: { tid: string; canWrite: boolean }) {
  const { message } = App.useApp();
  const roleOptions = useRoleLabelOptions();
  const contacts = useContacts(tid);
  const createContact = useCreateContact(tid);
  const updateContact = useUpdateContact(tid);
  const deleteContact = useDeleteContact(tid);
  const [form] = Form.useForm();
  const [editing, setEditing] = useState<ContactMeta | null>(null);
  const [open, setOpen] = useState(false);
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [delDone, setDelDone] = useState(0);
  // 从分组导入
  const groups = useContactGroups(canWrite);
  const importGroup = useImportGroup(tid);
  const [importOpen, setImportOpen] = useState(false);
  const [importGid, setImportGid] = useState<string | undefined>();

  const onImport = () => {
    if (!importGid) {
      message.warning('请选择分组');
      return;
    }
    importGroup.mutate(importGid, {
      onSuccess: (r) => {
        message.success(
          `已导入 ${r.imported} 个联系人${r.skipped ? `，跳过 ${r.skipped} 个已存在` : ''}`,
        );
        setImportOpen(false);
        setImportGid(undefined);
      },
      onError: (e) => message.error((e as Error).message || '导入失败'),
    });
  };

  const onBulkDelete = async () => {
    setBulkDeleting(true);
    setDelDone(0);
    let done = 0;
    const results = await Promise.allSettled(
      selectedKeys.map((id) =>
        deleteContact.mutateAsync(id).finally(() => {
          done += 1;
          setDelDone(done);
        }),
      ),
    );
    const ok = results.filter((r) => r.status === 'fulfilled').length;
    const fail = results.length - ok;
    setBulkDeleting(false);
    setSelectedKeys([]);
    if (fail === 0) message.success(`已删除 ${ok} 个联系人`);
    else message.warning(`删除 ${ok} 个，${fail} 个失败`);
  };

  const onSubmit = () =>
    form.validateFields().then((v) => {
      if (editing) {
        updateContact.mutate(
          { cid: editing.id, body: { ...v, expected_updated_at: editing.updated_at } },
          {
            onSuccess: () => {
              message.success('已更新');
              setOpen(false);
            },
            onError: () => message.error('更新失败'),
          },
        );
      } else {
        createContact.mutate(v, {
          onSuccess: () => {
            message.success('联系人已添加');
            setOpen(false);
            form.resetFields();
          },
          onError: () => message.error('添加失败'),
        });
      }
    });

  return (
    <>
      {canWrite && (
        <Space style={{ marginBottom: 12 }}>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              setEditing(null);
              form.resetFields();
              setOpen(true);
            }}
          >
            添加联系人
          </Button>
          <Button onClick={() => setImportOpen(true)}>从分组导入</Button>
          <ExcelImportButton<ContactImportRow>
            templateName="产品联系人导入模板"
            templateHeaders={CONTACT_TEMPLATE_HEADERS}
            templateSample={CONTACT_TEMPLATE_SAMPLE}
            parse={(wb, s) => parseContactSheet(wb, s, true)}
            importOne={async (row) => {
              await createContact.mutateAsync({
                email: row.email,
                name: row.name,
                org: row.org ?? '',
                ...(row.role_label ? { role_label: row.role_label } : {}),
                ...(row.note ? { note: row.note } : {}),
              });
            }}
            onDone={() => contacts.refetch()}
            label="导入联系人"
            previewColumns={[
              { title: '邮箱', dataIndex: 'email' },
              { title: '姓名', dataIndex: 'name' },
              { title: '单位', dataIndex: 'org' },
              { title: '角色标签', dataIndex: 'role_label' },
              { title: '备注', dataIndex: 'note' },
            ]}
          />
          {selectedKeys.length > 0 && (
            <Popconfirm
              title={`删除选中的 ${selectedKeys.length} 个联系人？`}
              okText="删除"
              okButtonProps={{ danger: true }}
              cancelText="取消"
              onConfirm={onBulkDelete}
            >
              <Button danger loading={bulkDeleting}>
                {bulkDeleting
                  ? `删除中 ${delDone}/${selectedKeys.length}`
                  : `批量删除 (${selectedKeys.length})`}
              </Button>
            </Popconfirm>
          )}
        </Space>
      )}
      <Table<ContactMeta>
        rowKey="id"
        loading={contacts.isLoading}
        dataSource={contacts.data ?? []}
        pagination={false}
        {...(canWrite
          ? {
              rowSelection: {
                selectedRowKeys: selectedKeys,
                onChange: (keys: Key[]) => setSelectedKeys(keys.map(String)),
              },
            }
          : {})}
        columns={[
          { title: '姓名', dataIndex: 'name', width: 120 },
          { title: '邮箱', dataIndex: 'email' },
          { title: '角色标签', dataIndex: 'role_label', width: 120 },
          { title: '单位', dataIndex: 'org', width: 90, render: (o: string) => <Tag>{o}</Tag> },
          {
            title: '启用',
            dataIndex: 'is_active',
            width: 80,
            render: (v: boolean) => (v ? <Tag color="green">是</Tag> : <Tag>否</Tag>),
          },
          { title: '备注', dataIndex: 'note', ellipsis: true, render: (v?: string) => v ?? '—' },
          ...(canWrite
            ? [
                {
                  title: '操作',
                  key: 'action',
                  width: 150,
                  render: (_: unknown, c: ContactMeta) => (
                    <Space size="small">
                      <Button
                        size="small"
                        type="link"
                        onClick={() => {
                          setEditing(c);
                          form.setFieldsValue(c);
                          setOpen(true);
                        }}
                      >
                        编辑
                      </Button>
                      <Popconfirm
                        title="删除该联系人？"
                        onConfirm={() =>
                          deleteContact.mutate(c.id, {
                            onSuccess: () => message.success('已删除'),
                            onError: () => message.error('删除失败'),
                          })
                        }
                      >
                        <Button size="small" type="link" danger>
                          删除
                        </Button>
                      </Popconfirm>
                    </Space>
                  ),
                },
              ]
            : []),
        ]}
      />

      <Modal
        title={editing ? `编辑联系人 ${editing.name}` : '添加联系人'}
        open={open}
        onOk={onSubmit}
        confirmLoading={createContact.isPending || updateContact.isPending}
        onCancel={() => setOpen(false)}
        destroyOnClose
      >
        <Form form={form} layout="vertical" preserve={false}>
          <Form.Item
            name="email"
            label="邮箱"
            rules={[{ required: true, type: 'email', message: '请输入有效邮箱' }]}
          >
            <Input disabled={!!editing} />
          </Form.Item>
          <Form.Item name="name" label="姓名" rules={[{ required: true, message: '请输入姓名' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="role_label" label="角色标签（选填）">
            <AutoComplete
              options={roleOptions}
              placeholder="选择或输入，如 采购 / 品管"
              filterOption={(input, opt) => (opt?.value ?? '').includes(input)}
            />
          </Form.Item>
          <Form.Item name="org" label="单位" rules={[{ required: true, message: '请输入单位' }]}>
            <Input placeholder="如 甲方 / 设计方 / 其他" />
          </Form.Item>
          <Form.Item name="note" label="备注">
            <Input.TextArea rows={2} />
          </Form.Item>
          {editing && (
            <Form.Item name="is_active" label="启用" valuePropName="checked">
              <Select
                options={[
                  { value: true, label: '启用' },
                  { value: false, label: '停用' },
                ]}
              />
            </Form.Item>
          )}
        </Form>
      </Modal>

      <Modal
        title="从分组导入联系人"
        open={importOpen}
        onOk={onImport}
        confirmLoading={importGroup.isPending}
        onCancel={() => setImportOpen(false)}
        destroyOnClose
      >
        <Typography.Paragraph type="secondary">
          选择一个联系人分组，将其下所有联系人导入本产品（按邮箱去重，已存在的跳过）。
        </Typography.Paragraph>
        <Select
          showSearch
          optionFilterProp="label"
          style={{ width: '100%' }}
          placeholder="选择分组（可搜索）"
          loading={groups.isLoading}
          value={importGid}
          onChange={setImportGid}
          options={(groups.data ?? []).map((g) => ({
            value: g.id,
            label: `${g.name}（${g.company}）· ${g.contact_count ?? 0} 人`,
          }))}
          notFoundContent={groups.isLoading ? '加载中…' : '暂无分组（请先在「联系人分组」创建）'}
        />
      </Modal>
    </>
  );
}

export function MembersContactsPage() {
  const tid = useSessionStore((s) => s.currentTenant?.id);
  const role = useSessionStore((s) => s.role);
  const isPlatformAdmin = useSessionStore((s) => s.user?.is_platform_admin ?? false);
  const [searchParams] = useSearchParams();
  // 成员：仅管理员/平台管理员；通知联系人：管理员/平台管理员/编辑
  const canManageMembers = isPlatformAdmin || role === 'admin';
  const canManageContacts = canManageMembers || role === 'editor';

  if (!tid) return <NoTenant />;

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Typography.Title level={4} style={{ margin: 0 }}>
        成员 / 联系人
      </Typography.Title>
      <Tabs
        defaultActiveKey={searchParams.get('tab') === 'contacts' ? 'contacts' : 'members'}
        items={[
          {
            key: 'members',
            label: '产品成员',
            children: <MembersTab tid={tid} canWrite={canManageMembers} />,
          },
          {
            key: 'contacts',
            label: '通知联系人',
            children: <ContactsTab tid={tid} canWrite={canManageContacts} />,
          },
        ]}
      />
    </Space>
  );
}
